import { createResource, Show, For } from "solid-js";
import { fetchShippedEvidence } from "../../api/shippedEvidence";

export interface ShippedEvidenceCardProps {
  workItemId: string;
}

/**
 * FLOW-534: read-only hint that git activity may already cover this item.
 *
 * Deliberately has no actions. It reports what was found and links out;
 * deciding what it means is the user's job, and changing status from a
 * hint is Phase 11's.
 */
export const ShippedEvidenceCard = (props: ShippedEvidenceCardProps) => {
  const [evidence] = createResource(
    () => props.workItemId,
    fetchShippedEvidence,
  );

  // Reading an errored resource throws, and the nearest ErrorBoundary is the
  // app root — a failed fetch for this optional hint must render nothing,
  // not blank the whole page. Gating on state === "ready" avoids ever
  // reading evidence() while it's in the error state.
  return (
    <Show when={evidence.state === "ready" && evidence()}>
      {(e) => (
        <div
          data-testid="shipped-evidence-card"
          class={
            "rounded border p-4 " +
            (e().confidence === "high"
              ? "border-signal-green/25 bg-signal-green-tint"
              : "border-line bg-paper")
          }
        >
          <div class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-1">
            Repository
          </div>
          <h3 class="text-sm font-semibold text-ink mb-2">Shipped evidence</h3>
          <Show
            when={e().confidence === "high"}
            fallback={
              <p class="text-sm text-ink-2">Recent activity may be related.</p>
            }
          >
            <p class="text-sm text-signal-green">
              This item appears to have shipped.
            </p>
          </Show>

          <Show when={e().rationale}>
            {(r) => <p class="mt-2 text-xs text-ink-2">{r()}</p>}
          </Show>

          <For each={e().pull_requests}>
            {(pr) => (
              <a
                href={pr.url}
                target="_blank"
                rel="noopener noreferrer"
                class="mt-2 flex items-baseline gap-2 text-sm text-ink hover:underline underline-offset-2 hit-area"
              >
                <span class="font-mono tabular-nums">#{pr.number}</span>
                <span class="truncate">{pr.title}</span>
              </a>
            )}
          </For>

          <Show when={e().commit_shas.length > 0}>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <For each={e().commit_shas.slice(0, 3)}>
                {(sha) => (
                  <span class="font-mono text-2xs text-ink-2 px-1 rounded-sm bg-surface border border-line">
                    {sha.slice(0, 7)}
                  </span>
                )}
              </For>
              <Show when={e().commit_shas.length > 3}>
                <span class="font-mono text-2xs text-ink-3">
                  +{e().commit_shas.length - 3}
                </span>
              </Show>
            </div>
          </Show>
        </div>
      )}
    </Show>
  );
};
